import { Link } from '@tanstack/react-router';
import { FileDown, Plus, Sparkles } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Discount } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';

import { useAddDiscountCodes, useExportDiscountCodes } from '../hooks/use-discount-mutations';
import { discountErrorTarget, isCode, uniqueCodes } from '../schemas/discount-rules';

import { DiscountCodeList } from './discount-code-list';
import { GenerateCodesDialog } from './generate-codes-dialog';

export interface DiscountCodesCardProps {
  storeId: string;
  discount: Discount;
  canWrite: boolean;
}

/**
 * Codes of a code discount, paged from the codes endpoint (a campaign can hold thousands), with
 * their state and use counts; new ones are typed or generated, the whole set exported as CSV.
 */
export function DiscountCodesCard({ storeId, discount, canWrite }: DiscountCodesCardProps) {
  const { t } = useTranslation(['discounts', 'errors']);
  const inputId = useId();
  const errorId = useId();
  const add = useAddDiscountCodes(storeId);
  const message = useErrorMessage();
  const [draft, setDraft] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const exportCodes = useExportDiscountCodes(storeId, discount.id);
  const exportError = exportCodes.error;

  const serverError = (() => {
    if (!add.error) return null;
    const target = discountErrorTarget(add.error);
    if (target) return t(target.key, target.values);
    return message(add.error);
  })();
  const error = localError ?? serverError;
  const requestId = isApiError(add.error) ? add.error.requestId : undefined;

  const submit = () => {
    const codes = uniqueCodes(
      draft
        .split(/[\s,]+/)
        .map((c) => c.trim())
        .filter(Boolean),
    );
    if (codes.length === 0) return;
    if (!codes.every(isCode)) {
      setLocalError(t('discounts:validation.codeFormat'));
      return;
    }
    setLocalError(null);
    add.mutate({ id: discount.id, body: { codes } }, { onSuccess: () => setDraft('') });
  };

  return (
    <FormSection
      title={t('discounts:sections.codes')}
      description={t('discounts:sections.codesDescription')}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium" data-testid="discount-code-count">
          {t('discounts:codes.count', { count: discount.codeCount })}
        </p>
        <Can permission="exports.run">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={exportCodes.isPending}
            onClick={() => exportCodes.mutate()}
          >
            <FileDown aria-hidden />
            {exportCodes.isPending ? t('discounts:codes.exporting') : t('discounts:codes.export')}
          </Button>
        </Can>
      </div>
      {exportCodes.isSuccess ? (
        <p className="text-muted-foreground text-sm" role="status">
          {t('discounts:codes.exportStarted')}{' '}
          <Link
            to="/s/$storeId/settings/imports-exports"
            params={{ storeId }}
            search={{ tab: 'exports' }}
            className="text-foreground underline"
          >
            {t('discounts:codes.exportOpen')}
          </Link>
        </p>
      ) : null}
      {exportError ? (
        <p className="text-destructive text-sm" role="alert">
          {message(exportError)}
          {isApiError(exportError) ? (
            <span className="text-muted-foreground block text-xs">
              {t('errors:requestIdShort', { requestId: exportError.requestId })}
            </span>
          ) : null}
        </p>
      ) : null}
      <DiscountCodeList storeId={storeId} discountId={discount.id} canWrite={canWrite} />
      {canWrite ? (
        <div className="flex flex-col gap-2 border-t pt-4">
          <Label htmlFor={inputId}>{t('discounts:codes.add')}</Label>
          <div className="flex flex-wrap gap-2">
            <Input
              id={inputId}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setLocalError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={t('discounts:fields.codesPlaceholder')}
              className="max-w-xs font-mono"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            />
            <Button
              type="button"
              variant="outline"
              disabled={add.isPending || draft.trim() === ''}
              onClick={submit}
            >
              <Plus aria-hidden />
              {t('discounts:codes.addSubmit')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setGenerating(true)}>
              <Sparkles aria-hidden />
              {t('discounts:codes.generate')}
            </Button>
          </div>
          {error ? (
            <p id={errorId} className="text-destructive text-sm" role="alert">
              {error}
              {requestId && !localError ? (
                <span className="text-muted-foreground block text-xs">
                  {t('errors:requestIdShort', { requestId })}
                </span>
              ) : null}
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">{t('discounts:fields.codesHint')}</p>
          )}
          <GenerateCodesDialog
            storeId={storeId}
            discountId={discount.id}
            open={generating}
            onOpenChange={setGenerating}
          />
        </div>
      ) : null}
    </FormSection>
  );
}
